import { useState, useEffect } from 'react';
import { crmService } from '../../services/crmService';

export default function OutlookSync() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [error, setError] = useState('');

  const loadStatus = async () => {
    try {
      setLoading(true);
      const s = await crmService.getOutlookStatus();
      setStatus(s);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const handleConnect = async () => {
    try {
      const { url, error: urlError } = await crmService.getOutlookAuthUrl();
      if (urlError) { setError(urlError); return; }
      window.location.href = url;
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setSyncResult(null);
      setError('');
      const result = await crmService.syncOutlook();
      setSyncResult(result);
      await loadStatus();
    } catch (err) {
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  };

  const box = {
    background: '#111827',
    border: '1px solid #1E2D45',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '16px',
    color: '#F1F5F9',
  };

  return (
    <div style={{ maxWidth: '600px', margin: '24px auto', padding: '0 16px' }}>
      <h2 style={{ color: '#22D3EE', fontSize: '18px', marginBottom: '20px' }}>Outlook Integration</h2>

      {/* Connection status */}
      <div style={box}>
        <h3 style={{ margin: '0 0 12px', fontSize: '14px', color: '#94A3B8' }}>Connection Status</h3>
        {loading ? (
          <div style={{ color: '#6B7280' }}>Checking connection…</div>
        ) : status?.connected ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: status.expired ? '#EF4444' : '#10B981' }} />
              <span style={{ color: status.expired ? '#EF4444' : '#10B981', fontWeight: '600' }}>
                {status.expired ? 'Token expired — reconnect required' : 'Connected'}
              </span>
            </div>
            {status.last_sync && (
              <div style={{ color: '#6B7280', fontSize: '12px' }}>
                Last sync: {new Date(status.last_sync).toLocaleString()}
              </div>
            )}
          </div>
        ) : (
          <div style={{ color: '#6B7280' }}>Not connected</div>
        )}
      </div>

      {/* Connect / Reconnect */}
      {(!status?.connected || status?.expired) && (
        <div style={box}>
          <h3 style={{ margin: '0 0 8px', fontSize: '14px', color: '#94A3B8' }}>Connect Microsoft Outlook</h3>
          <p style={{ color: '#6B7280', fontSize: '12px', margin: '0 0 12px', lineHeight: '1.5' }}>
            Connect your Outlook account to automatically sync calendar meetings and email interactions
            with your CRM contacts. This uses Microsoft Graph API with read-only permissions for
            calendar events, emails, and contacts.
          </p>
          <div style={{ background: '#0A0F1C', borderRadius: '6px', padding: '12px', marginBottom: '14px', fontSize: '11px', color: '#94A3B8' }}>
            <div style={{ fontWeight: '600', color: '#22D3EE', marginBottom: '6px' }}>Required Azure App Registration</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '4px' }}>
              {['MICROSOFT_CLIENT_ID', 'MICROSOFT_CLIENT_SECRET', 'MICROSOFT_TENANT_ID', 'MICROSOFT_REDIRECT_URI']
                .map(v => (
                  <div key={v} style={{ background: '#111827', padding: '4px 8px', borderRadius: '3px', fontFamily: 'monospace', fontSize: '10px', color: '#6B7280' }}>
                    {v}
                  </div>
                ))}
            </div>
            <div style={{ marginTop: '8px', color: '#4B5563', fontSize: '10px' }}>
              Register at portal.azure.com → Azure Active Directory → App registrations
            </div>
          </div>
          <button onClick={handleConnect}
            style={{ padding: '8px 20px', background: '#0078D4', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
            Connect Outlook Account
          </button>
        </div>
      )}

      {/* Sync */}
      {status?.connected && !status?.expired && (
        <div style={box}>
          <h3 style={{ margin: '0 0 8px', fontSize: '14px', color: '#94A3B8' }}>Sync Now</h3>
          <p style={{ color: '#6B7280', fontSize: '12px', margin: '0 0 12px', lineHeight: '1.5' }}>
            Syncs the last 90 days of calendar events and links them to matching CRM contacts
            by email address. Also updates contact-to-Outlook-ID mappings.
          </p>
          <button onClick={handleSync} disabled={syncing}
            style={{ padding: '8px 20px', background: syncing ? '#374151' : '#22D3EE', color: syncing ? '#94A3B8' : '#0A0F1C', border: 'none', borderRadius: '4px', cursor: syncing ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '600' }}>
            {syncing ? 'Syncing…' : 'Sync Calendar & Contacts'}
          </button>

          {syncResult && (
            <div style={{ marginTop: '12px', background: '#0A0F1C', borderRadius: '6px', padding: '12px' }}>
              <div style={{ color: '#10B981', fontWeight: '600', marginBottom: '6px' }}>Sync Complete</div>
              <div style={{ color: '#94A3B8', fontSize: '12px', display: 'flex', gap: '20px' }}>
                <div>
                  <span style={{ color: '#6B7280' }}>Contacts matched: </span>
                  <span style={{ color: '#F1F5F9', fontWeight: '600' }}>{syncResult.synced_contacts}</span>
                </div>
                <div>
                  <span style={{ color: '#6B7280' }}>Interactions created: </span>
                  <span style={{ color: '#F1F5F9', fontWeight: '600' }}>{syncResult.synced_interactions}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* What gets synced */}
      <div style={box}>
        <h3 style={{ margin: '0 0 12px', fontSize: '14px', color: '#94A3B8' }}>What Gets Synced</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {[
            { icon: '📅', title: 'Calendar Events → Interactions', desc: 'Meetings with CRM contacts become logged interactions, visible in the contact timeline and network graph.' },
            { icon: '📧', title: 'Contact Email Matching', desc: 'Outlook contacts are matched by email to your CRM contacts and linked for future sync.' },
            { icon: '🔄', title: 'Last Interaction Date', desc: 'Contact "last interaction date" is automatically updated based on synced meetings.' },
          ].map(item => (
            <div key={item.title} style={{ display: 'flex', gap: '12px' }}>
              <span style={{ fontSize: '18px', flexShrink: 0 }}>{item.icon}</span>
              <div>
                <div style={{ fontWeight: '600', fontSize: '12px', color: '#F1F5F9', marginBottom: '2px' }}>{item.title}</div>
                <div style={{ fontSize: '11px', color: '#6B7280', lineHeight: '1.4' }}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {error && (
        <div style={{ background: '#1f1015', border: '1px solid #7F1D1D', borderRadius: '6px', padding: '12px', color: '#EF4444', fontSize: '12px' }}>
          {error}
        </div>
      )}
    </div>
  );
}
